import { useRef, useState, type ReactNode } from 'react';
import {
  Platform,
  Pressable,
  StyleSheet,
  TextInput,
  View,
  type TextInputProps,
} from 'react-native';

import { assertHitOnLayout } from '../a11y';
import { Icon } from '../icons/Icon';
import type { IconName } from '../icons/paths';
import { parentType, textStyleFor } from '../theme/typography';
import { colors, hit, radii, strokes } from '../tokens';
import { KEYBOARD_DONE_ID, useReturnKey } from './keyboard';
import { HelperText, Label, Text } from './Text';

export interface TextFieldProps extends Omit<
  TextInputProps,
  'style' | 'secureTextEntry' | 'placeholderTextColor'
> {
  /** Field label above the box ("Family name", 12/600 slate); omitted for the P02 icon fields. */
  label?: string;
  /** Leading glyph inside the box (envelope, lock on P02). */
  leading?: IconName;
  /** Password field with the eye toggle (44×44 box). */
  secure?: boolean;
  /** Error state. A string renders under the field in #F53827 (form errors only; never Oops orange); `true` is the red border alone (copy pending). */
  error?: string | boolean;
  helper?: string;
  /** Right-hand slot inside the box (e.g. a Badge). */
  trailing?: ReactNode;
  /** Labels for the eye toggle — copy pending (plan §8 a11y gaps); omit until supplied. */
  toggleSecureLabel?: { show: string; hide: string };
  /**
   * **A WORD INSTEAD OF THE EYE** (board P02e/P06e, 25 Sep: *"typed once with Show"*). Implies `secure`. The word
   * does not change with the state: it is a switch named "Show", and whether it is on is `accessibilityState`,
   * so the board's one word is the whole of the copy.
   */
  showToggle?: string;
}

/**
 * White box 52 tall, radius 12, side padding 14, 18px leading glyph (board P02); the border is always 2px so focus
 * never shifts content (board draws 1.5): quiet grey idle, deep teal focused, error red on error. Typed value
 * 15/600 Poppins; RN cannot weight the placeholder separately, so it renders at the input weight (board: 400).
 */
export function TextField({
  label,
  leading,
  secure = false,
  error,
  helper,
  trailing,
  toggleSecureLabel,
  showToggle,
  onFocus,
  onBlur,
  ...input
}: TextFieldProps) {
  const [focused, setFocused] = useState(false);
  const ref = useRef<TextInput>(null);
  // Next / Done from the form's `FieldChain`, unless the caller set its own (keyboard.tsx).
  const returnKey = useReturnKey(ref, input.multiline);
  const [hidden, setHidden] = useState(secure || showToggle !== undefined);
  const hasError = !!error;
  const errorMessage = typeof error === 'string' ? error.trim() : '';
  const borderColor = hasError ? colors.error : focused ? colors.deepTeal : colors.quietBorder;
  const inputStyle = textStyleFor(parentType.input);
  if (Platform.OS === 'android') delete inputStyle.lineHeight; // Android clips Poppins descenders inside a TextInput with lineHeight
  return (
    <View style={styles.wrap}>
      {label ? <Label tone="slate">{label}</Label> : null}
      <View style={[styles.box, { borderColor }]}>
        {leading ? <Icon name={leading} size={18} color={colors.muted} /> : null}
        <TextInput
          ref={ref}
          {...returnKey}
          {...input}
          // The Done bar above every keyboard, number pads included (build 25 walk) — never overridden.
          inputAccessoryViewID={KEYBOARD_DONE_ID}
          secureTextEntry={hidden}
          placeholderTextColor={colors.muted}
          onFocus={(e) => {
            setFocused(true);
            onFocus?.(e);
          }}
          onBlur={(e) => {
            setFocused(false);
            onBlur?.(e);
          }}
          style={[styles.input, inputStyle]}
          accessibilityLabel={input.accessibilityLabel ?? label ?? input.placeholder}
        />
        {showToggle !== undefined ? (
          <Pressable
            onPress={() => setHidden((h) => !h)}
            accessibilityRole="switch"
            accessibilityState={{ checked: !hidden }}
            accessibilityLabel={showToggle}
            onLayout={assertHitOnLayout('TextField.show', hit.parent)}
            style={[styles.eye, styles.word]}
          >
            <Text variant="bodySm" tone={hidden ? 'slate' : 'ink'}>
              {showToggle}
            </Text>
          </Pressable>
        ) : secure ? (
          <Pressable
            onPress={() => setHidden((h) => !h)}
            accessibilityRole="switch"
            accessibilityState={{ checked: !hidden }}
            accessibilityLabel={hidden ? toggleSecureLabel?.show : toggleSecureLabel?.hide}
            onLayout={assertHitOnLayout('TextField.eye', hit.parent)}
            style={styles.eye}
          >
            <Icon name={hidden ? 'eye' : 'eye-off'} size={22} color={colors.muted} />
          </Pressable>
        ) : null}
        {trailing}
      </View>
      {errorMessage ? (
        <HelperText tone="error">{errorMessage}</HelperText>
      ) : helper ? (
        <HelperText tone="muted">{helper}</HelperText>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: { gap: 6 },
  box: {
    minHeight: 52,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 14,
    backgroundColor: colors.white,
    borderRadius: radii.input,
    borderWidth: strokes.playful,
  },
  input: { flex: 1, color: colors.ink, paddingVertical: 12 },
  // A word is wider than the eye: keep the 44 hit box as a floor, not a width.
  word: { width: undefined, minWidth: hit.parent, paddingHorizontal: 4 },
  eye: {
    width: hit.parent,
    minHeight: hit.parent,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: -(hit.parent - 22) / 2,
  },
});
